import { useState, useEffect } from "react";
import {
  Search,
  Filter,
  ArrowUpDown,
  MoreVertical,
  Eye,
  BookOpen,
  FileText,
  HelpCircle,
  ClipboardList,
  Edit,
  Copy,
  Trash2,
} from "lucide-react";
import { Button } from "@/components/admin/ui/Button";
import { Input } from "@/components/admin/ui/Input";
import { Select } from "@/components/admin/ui/Select";
import { Badge } from "@/components/admin/ui/Badge";
import { Avatar } from "@/components/admin/ui/Avatar";
import { useToast } from "@/components/admin/ui/Toast";
import { courses } from "@/data/adminMock";

interface CourseListProps {
  onNewCourse: () => void;
  onEditCourse?: (id: number) => void;
  onNewBundle: () => void;
  courseList?: any[];
  setCourseList?: React.Dispatch<React.SetStateAction<any[]>>;
  showOnlyBundles?: boolean;
}

export function CourseList({
  onNewCourse,
  onEditCourse,
  onNewBundle,
  courseList: propCourseList,
  setCourseList: propSetCourseList,
  showOnlyBundles = false,
}: CourseListProps) {
  const [localCourseList, setLocalCourseList] = useState(courses);
  const courseList = propCourseList !== undefined ? propCourseList : localCourseList;
  const setCourseList = propSetSetCourseList(propSetCourseList) || setLocalCourseList;
  function propSetSetCourseList(fn: any) {
    return fn;
  }
  const [selected, setSelected] = useState<number[]>([]);
  const [search, setSearch] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [bulkAction, setBulkAction] = useState("");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const [openMenuId, setOpenMenuId] = useState<number | null>(null);
  const { showToast } = useToast();

  const normalizeTitle = (title: string) => {
    if (!title) return "";
    return title
      .replace(/[–—]/g, "-") // replace en-dash and em-dash with hyphen-minus
      .replace(/\s+/g, " ")   // collapse multiple whitespaces
      .trim()
      .toLowerCase();
  };

  const getCourseUrl = (course: any) => {
    const urls = (window as any).shLmsAdmin?.course_urls;
    if (urls) {
      if (urls[course.title]) {
        return urls[course.title];
      }
      const normalizedTitle = normalizeTitle(course.title);
      if (urls[normalizedTitle]) {
        return urls[normalizedTitle];
      }
    }
    return `${(window as any).shLmsAdmin?.home_url || "/"}?post_type=courses&p=${course.id}`;
  };

  const [dbInstructors, setDbInstructors] = useState<any[]>([]);

  const fetchInstructors = () => {
    const ajaxUrl = (window as any).hubInstructor?.ajax_url || (window as any).ajaxurl;
    const nonce = (window as any).hubInstructor?.nonce;
    if (ajaxUrl && nonce && (window as any).jQuery) {
      (window as any).jQuery.ajax({
        url: ajaxUrl,
        method: "GET",
        data: {
          action: "hub_get_instructors",
          security: nonce
        },
        success: (res: any) => {
          if (res.success && Array.isArray(res.data)) {
            setDbInstructors(res.data);
          }
        },
        error: (err: any) => {
          console.error("Failed to fetch instructors for course list:", err);
        }
      });
    }
  };

  useEffect(() => {
    fetchInstructors();
    (window as any).refreshCourseListInstructors = fetchInstructors;
    return () => {
      delete (window as any).refreshCourseListInstructors;
    };
  }, []);

  const categories = Array.from(new Set(courses.map((c) => c.category)));

  // Filter logic
  const filteredCourses = courseList.filter((course) => {
    const matchesSearch =
      course.title.toLowerCase().includes(search.toLowerCase()) ||
      course.category.toLowerCase().includes(search.toLowerCase());
    const matchesCategory = selectedCategory ? course.category === selectedCategory : true;
    const matchesStatus = selectedStatus ? course.status === selectedStatus : true;
    const matchesBundleType = showOnlyBundles ? (course.isBundle || course.post_type === "course_bundle") : true;
    return matchesSearch && matchesCategory && matchesStatus && matchesBundleType;
  });

  // Sort logic (by date)
  const sortedCourses = [...filteredCourses].sort((a, b) => {
    const dateA = new Date(a.date).getTime();
    const dateB = new Date(b.date).getTime();
    return sortDirection === "asc" ? dateA - dateB : dateB - dateA;
  });

  const toggleAll = () => {
    if (selected.length === sortedCourses.length) {
      setSelected([]);
    } else {
      setSelected(sortedCourses.map((c) => c.id));
    }
  };

  const toggleOne = (id: number) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleBulkApply = () => {
    if (!bulkAction || selected.length === 0) return;
    if (bulkAction === "publish") {
      setCourseList((prev: any[]) =>
        prev.map((c: any) => (selected.includes(c.id) ? { ...c, status: "Publish" } : c))
      );
    } else if (bulkAction === "draft") {
      setCourseList((prev: any[]) =>
        prev.map((c: any) => (selected.includes(c.id) ? { ...c, status: "Draft" } : c))
      );
    } else if (bulkAction === "delete") {
      setCourseList((prev: any[]) => prev.filter((c: any) => !selected.includes(c.id)));
    }
    setSelected([]);
    setBulkAction("");
  };

  const handleStatusChange = (id: number, newStatus: string) => {
    setCourseList((prev: any[]) =>
      prev.map((c: any) => (c.id === id ? { ...c, status: newStatus } : c))
    );
  };

  return (
    <div className="p-6 lg:p-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#1A1F2C]">
            {showOnlyBundles ? "Course Bundles" : "Courses"}
          </h1>
          <p className="text-sm text-[#5A6A85] mt-1">
            {showOnlyBundles
              ? "Manage your custom course bundles and pricing options"
              : "Manage your courses, bundles, and curriculum"}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="secondary" onClick={onNewBundle}>
            New Bundle
          </Button>
          {!showOnlyBundles && (
            <Button
              leftIcon={<span className="text-lg leading-none">+</span>}
              onClick={onNewCourse}
            >
              New Course
            </Button>
          )}
        </div>
      </div>

      {/* Table Card */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden transition-colors">
        {/* Filter Toolbar */}
        <div className="p-4 border-b border-gray-100 flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Select
              options={[
                { value: "", label: "Bulk Action" },
                { value: "publish", label: "Publish" },
                { value: "draft", label: "Move to Draft" },
                { value: "delete", label: "Delete" },
              ]}
              value={bulkAction}
              onChange={(val) => setBulkAction(val)}
              className="w-40"
            />
            <Button variant="outline" size="sm" onClick={handleBulkApply} disabled={selected.length === 0}>
              Apply
            </Button>
            {selected.length > 0 && (
              <span className="text-xs text-gray-500">
                {selected.length} selected
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant={filtersOpen ? "primary" : "outline"}
              size="sm"
              leftIcon={<Filter className="h-4 w-4" />}
              onClick={() => setFiltersOpen(!filtersOpen)}
            >
              Filters
            </Button>
            <button
              onClick={() => setSortDirection(prev => prev === "asc" ? "desc" : "asc")}
              className={`p-2 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 transition-colors flex items-center gap-1.5 text-xs font-medium ${sortDirection === "asc" ? "bg-red-50 text-[#ff0000] border-red-200" : ""}`}
              title="Sort by date"
            >
              <ArrowUpDown className="h-4 w-4" />
              <span>{sortDirection === "asc" ? "Oldest" : "Newest"}</span>
            </button>
            <Input
              placeholder="Search courses..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              leftIcon={<Search className="h-4 w-4" />}
              containerClassName="w-64"
            />
          </div>
        </div>

        {/* Dynamic Filters panel */}
        {filtersOpen && (
          <div className="p-4 bg-gray-50/70 border-b border-gray-100 flex flex-wrap gap-4 items-center animate-in fade-in slide-in-from-top-2 duration-200">
            <div className="flex flex-col gap-1.5">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Category</span>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#ff0000]/20 focus:border-[#ff0000]"
              >
                <option value="">All Categories</option>
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-1.5">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</span>
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#ff0000]/20 focus:border-[#ff0000]"
              >
                <option value="">All Statuses</option>
                <option value="Publish">Publish</option>
                <option value="Draft">Draft</option>
              </select>
            </div>
            {(selectedCategory || selectedStatus) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSelectedCategory("");
                  setSelectedStatus("");
                }}
                className="mt-5 text-gray-500 hover:text-[#ff0000]"
              >
                Reset Filters
              </Button>
            )}
          </div>
        )}

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <th className="px-5 py-3 w-10">
                  <input
                    type="checkbox"
                    checked={
                      selected.length === sortedCourses.length && sortedCourses.length > 0
                    }
                    onChange={toggleAll}
                    className="rounded border-gray-300 text-[#ff0000] focus:ring-[#ff0000]/30"
                  />
                </th>
                <th className="px-5 py-3 font-medium">Title</th>
                <th className="px-5 py-3 font-medium">Categories</th>
                <th className="px-5 py-3 font-medium">Price</th>
                <th className="px-5 py-3 font-medium">Author</th>
                <th className="px-5 py-3 font-medium">Date</th>
                <th className="px-5 py-3 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {sortedCourses.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-5 py-8 text-center text-gray-500">
                    No courses found matching the search criteria.
                  </td>
                </tr>
              ) : (
                sortedCourses.map((course) => {
                  const dbInst = Array.isArray(dbInstructors)
                    ? dbInstructors.find(
                        (i) =>
                          i &&
                          ((i.name && i.name === course.author) ||
                            (i.raw_name && i.raw_name === course.author) ||
                            (i.id && i.id === course.instructor_id))
                      )
                    : null;
                  const displayName = dbInst ? dbInst.name : course.author;
                  const avatarUrl = dbInst ? dbInst.avatar : course.authorAvatar;

                  return (
                    <tr
                      key={course.id}
                      className="hover:bg-gray-50/60 transition-colors"
                    >
                      <td className="px-5 py-4">
                        <input
                          type="checkbox"
                          checked={selected.includes(course.id)}
                          onChange={() => toggleOne(course.id)}
                          className="rounded border-gray-300 text-[#ff0000] focus:ring-[#ff0000]/30"
                        />
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex items-start gap-3 min-w-[280px]">
                          {course.thumbnail && !course.thumbnail.includes("544716278-ca5e3f4abd8c") ? (
                            <img
                              src={course.thumbnail}
                              alt={course.title}
                              className="h-14 w-20 rounded-lg object-cover border border-gray-100 flex-shrink-0"
                            />
                          ) : (
                            <div className="h-14 w-20 rounded-lg bg-gray-50 border border-gray-200 flex items-center justify-center flex-shrink-0 text-gray-400">
                              <BookOpen className="h-6 w-6" />
                            </div>
                          )}
                          <div>
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="font-semibold text-gray-800">
                                {course.title}
                              </p>
                              {(course.isBundle || course.post_type === "course_bundle") && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-[#ff0000]/10 text-[#ff0000] border border-[#ff0000]/20">
                                  Bundle
                                </span>
                              )}
                            </div>
                            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5 text-xs text-gray-500">
                              <span className="flex items-center gap-1">
                                <BookOpen className="h-3 w-3" /> {course.topics}{" "}
                                Topics
                              </span>
                              <span className="flex items-center gap-1">
                                <FileText className="h-3 w-3" /> {course.lessons}{" "}
                                Lessons
                              </span>
                              <span className="flex items-center gap-1">
                                <HelpCircle className="h-3 w-3" /> {course.quizzes}{" "}
                                Quiz
                              </span>
                              <span className="flex items-center gap-1">
                                <ClipboardList className="h-3 w-3" />{" "}
                                {course.assignments} Assignment
                              </span>
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-4">
                        <Badge variant="blue">{course.category}</Badge>
                      </td>
                      <td className="px-5 py-4 font-medium text-gray-800 font-english">
                        ৳{(Number(course.price) || 0).toLocaleString()}
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-2">
                          <Avatar
                            src={avatarUrl}
                            name={displayName}
                            size="sm"
                          />
                          <span className="text-gray-700">
                            {displayName}
                          </span>
                        </div>
                      </td>
                      <td className="px-5 py-4 text-gray-500">
                        {course.date}
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-2">
                          <div className="relative">
                            <select
                              value={course.status}
                              onChange={(e) => handleStatusChange(course.id, e.target.value)}
                              className="appearance-none rounded-full border border-gray-200 bg-white pl-7 pr-7 py-1.5 text-xs font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#ff0000]/10 min-w-[105px]"
                            >
                              <option value="Publish">Publish</option>
                              <option value="Draft">Draft</option>
                            </select>
                            <span
                              className={`absolute left-2 top-1/2 -translate-y-1/2 h-2 w-2 rounded-full ${
                                course.status === "Publish"
                                  ? "bg-green-500"
                                  : "bg-gray-400"
                              }`}
                            />
                            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 text-[10px]">
                              ▼
                            </span>
                          </div>
                          <a
                            href={getCourseUrl(course)}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <Button
                              variant="ghost"
                              size="sm"
                              leftIcon={<Eye className="h-4 w-4" />}
                            >
                              View
                            </Button>
                          </a>
                          <div className="relative">
                            <button
                              onClick={() => setOpenMenuId(openMenuId === course.id ? null : course.id)}
                              className="p-1.5 text-[#94A3B8] hover:text-[#374151] hover:bg-gray-100 rounded-lg transition-colors"
                            >
                              <MoreVertical className="h-4 w-4" />
                            </button>
                            {openMenuId === course.id && (
                              <>
                                <div className="fixed inset-0 z-40" onClick={() => setOpenMenuId(null)} />
                                <div className="absolute right-0 top-full mt-1 w-44 bg-white border border-[#E2E8F0] rounded-lg shadow-lg z-50 py-1 animate-in fade-in slide-in-from-top-1 duration-150">
                                  <button
                                    onClick={() => {
                                      setOpenMenuId(null);
                                      onEditCourse?.(course.id);
                                    }}
                                    className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-[#374151] hover:bg-gray-50 transition-colors"
                                  >
                                    <Edit className="h-3.5 w-3.5" /> Edit
                                  </button>
                                  <button
                                    onClick={() => { setOpenMenuId(null); showToast("Course duplicated!", "success"); }}
                                    className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-[#374151] hover:bg-gray-50 transition-colors"
                                  >
                                    <Copy className="h-3.5 w-3.5" /> Duplicate
                                  </button>
                                  <a
                                    href={getCourseUrl(course)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={() => setOpenMenuId(null)}
                                    className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-[#374151] hover:bg-gray-50 transition-colors no-underline"
                                  >
                                    <Eye className="h-3.5 w-3.5" /> View Frontend
                                  </a>
                                  <hr className="my-1 border-gray-100" />
                                  <button
                                    onClick={() => {
                                      if (confirm("Are you sure you want to delete this course?")) {
                                        const ajaxUrl = (window as any).shLmsAdmin?.ajax_url || (window as any).ajaxurl;
                                        const nonce = (window as any).shLmsAdmin?.nonce || (window as any).chorchaAdmin?.nonce;
                                        if (ajaxUrl && nonce && (window as any).jQuery) {
                                          (window as any).jQuery.ajax({
                                            url: ajaxUrl,
                                            method: "POST",
                                            data: {
                                              action: "chorcha_delete_course",
                                              nonce: nonce,
                                              post_id: course.id
                                            },
                                            success: (res: any) => {
                                              if (res.success) {
                                                setCourseList((prev: any[]) => prev.filter((c: any) => c.id !== course.id));
                                                showToast("Course deleted successfully.", "success");
                                              } else {
                                                showToast(res.data || "Failed to delete course.", "error");
                                              }
                                            },
                                            error: () => {
                                              showToast("Error trashing course.", "error");
                                            }
                                          });
                                        } else {
                                          // dev/fallback
                                          setCourseList((prev: any[]) => prev.filter((c: any) => c.id !== course.id));
                                          showToast("Course deleted.", "error");
                                        }
                                        setOpenMenuId(null);
                                      }
                                    }}
                                    className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                                  >
                                    <Trash2 className="h-3.5 w-3.5" /> Delete
                                  </button>
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
